import React, { useState, useEffect, useMemo } from 'react';
import { SKIP_SCROLL_RESTORE_KEY } from '../common/ScrollRestoration';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Badge,
  Tooltip,
  Stack,
  Container,
  Grid
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import axios from 'axios';
import config from '../../config';
import { searchProductsForSupplement, findSupplements } from './amazonApi';
import { formatSupplementName, generateProductSlug } from '../../utils/productUtils';
import Autocomplete from '@mui/material/Autocomplete';
import ProductFilters, { 
  DEFAULT_FILTERS, 
  getFilterCount, 
  applyFilters,
  DIETARY_FIELD_MAP
} from './ProductFilters';
import { styles } from './ProductsExplorer.styles';
import ProductCard from './ProductCard';
import { Helmet } from 'react-helmet-async';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Collapse from '@mui/material/Collapse';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { alpha } from '@mui/material/styles';
import ClearIcon from '@mui/icons-material/Clear';
import { logAnalyticsEvent } from '../../utils/analytics';
import { color, radius, r } from '../../theme/tokens';
import BackToTop from '../common/BackToTop';

// Constants
const PARALLEL_LOAD_SIZE = 5;   // Number of supplements to load in parallel
const PRODUCT_CACHE_TTL = 1000 * 60 * 5;  // 5 minutes cache TTL
const SCROLL_THRESHOLD = 800; // pixels from bottom to trigger next load
const INITIAL_SUPPLEMENTS_TO_LOAD = 2;
const SUPPLEMENTS_PER_BATCH = 3;

const encodePathSegment = (value) => encodeURIComponent(String(value || '').trim());

// Product cache
const productCache = new Map();

// Helper function to load products for multiple supplements in parallel
const loadProductsBatch = async (supplements, startIndex, batchSize) => {
  const batch = supplements.slice(startIndex, startIndex + batchSize);
  const results = [];
  const processedKeys = new Set(); // Track processed supplement keys
  
  // First, check cache and add cached products
  batch.forEach(s => {
    const cached = productCache.get(s.key);
    if (cached && Date.now() - cached.timestamp < PRODUCT_CACHE_TTL) {
      results.push(...cached.products);
      processedKeys.add(s.key);
    }
  });
  
  // Get supplements that need to be fetched
  const uncachedSupplements = batch.filter(s => !processedKeys.has(s.key));
  
  // Load uncached products in parallel with a controlled batch size
  for (let i = 0; i < uncachedSupplements.length; i += PARALLEL_LOAD_SIZE) {
    const batchPromises = uncachedSupplements
      .slice(i, i + PARALLEL_LOAD_SIZE)
      .map(async (supplement) => {
        try {
          const products = await searchProductsForSupplement(supplement.key);
          if (products.length > 0) {
            // Add source information to products
            const productsWithSource = products.map(p => ({
              ...p,
              sourceSupplement: supplement.name,
              supplementKey: supplement.key,
              id: p.id || `${supplement.key}-${Math.random().toString(36).substring(2, 10)}`
            }));
            
            // Cache the results
            productCache.set(supplement.key, {
              products: productsWithSource,
              timestamp: Date.now()
            });
            
            return productsWithSource;
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') { console.error(`Error loading products for ${supplement.name}:`, error); }
        }
        return [];
      });
      
    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults.flat());
  }
  
  // Remove any potential duplicates by ID
  const uniqueProducts = Array.from(
    new Map(results.map(product => [product.id, product])).values()
  );
  
  return uniqueProducts;
};

const ProductsExplorer = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { supplementKey: routeSupplementKey } = useParams();
  const [searchParams] = useSearchParams();
  
  // Screen size states
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth < theme.breakpoints.values.sm;
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  // Products per page constants
  const MOBILE_PRODUCTS_PER_PAGE = 6;
  const DESKTOP_PRODUCTS_PER_PAGE = 12;
  const productsPerPage = isMobile ? MOBILE_PRODUCTS_PER_PAGE : DESKTOP_PRODUCTS_PER_PAGE;
  
  // Supplement data states
  const [supplements, setSupplements] = useState([]);
  const [categories, setCategories] = useState([]);
  const [popularSupplements, setPopularSupplements] = useState([]);
  const [supplementsConfig, setSupplementsConfig] = useState([]);
  const [discoveredSupplements, setDiscoveredSupplements] = useState([]);
  const [selectedSupplements, setSelectedSupplements] = useState([]);
  
  // Product data states
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDuration, setLoadingDuration] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [searchOptions, setSearchOptions] = useState([]);
  
  // Filter states
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // Pagination states
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Infinite scroll states
  const [currentSupplementIndex, setCurrentSupplementIndex] = useState(0);
  const [hasMoreSupplements, setHasMoreSupplements] = useState(true);
  const [seenSupplements, setSeenSupplements] = useState(new Set());
  
  // Misc states
  const [activeCategory, setActiveCategory] = useState('all');
  
  // Calculate filter count using the helper function
  const filterCount = getFilterCount(filters);
  
  // Track window size changes
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Track loading duration for user feedback
  useEffect(() => {
    let interval;
    
    if (loading && products.length === 0) {
      // Start tracking when initial load begins
      setLoadingDuration(0);
      
      interval = setInterval(() => {
        setLoadingDuration(prev => prev + 1000);
      }, 1000);
    } else {
      // Reset when loading completes
      setLoadingDuration(0);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loading, products.length]);
  
  // Update pagination when filtered products or products per page changes
  useEffect(() => {
    if (filteredProducts.length > 0) {
      const newTotalPages = Math.max(1, Math.ceil(filteredProducts.length / productsPerPage));
      setTotalPages(newTotalPages);
      
      // If current page would be beyond the total, adjust it
      if (page > newTotalPages) {
        setPage(Math.max(1, newTotalPages));
      }
    }
  }, [filteredProducts.length, productsPerPage, page]);
  
  // Get current page products
  const getCurrentPageProducts = () => {
    if (filteredProducts.length === 0) return [];
    
    // If no specific supplements are selected and we're showing the grouped view,
    // don't paginate - show all products to avoid cutting off supplement groups
    if (selectedSupplements.length === 0) {
      return filteredProducts;
    }
    
    // Only paginate when showing specific selected supplements
    const startIndex = (page - 1) * productsPerPage;
    const endIndex = Math.min(startIndex + productsPerPage, filteredProducts.length);
    
    return filteredProducts.slice(startIndex, endIndex);
  };
  
  // Load supplements data
  useEffect(() => {
    const loadSupplements = async () => {
      try {
        const response = await axios.get(`${config.FIREBASE_STORAGE_BASE_URL}config%2Fsupplements.json?alt=media`);
        const supplementsData = response.data;
        
        if (Array.isArray(supplementsData)) {
          // Sort supplements by name
          const sortedSupplements = supplementsData.sort((a, b) => 
            a.name.localeCompare(b.name)
          );
          
          setSupplements(sortedSupplements);
          setSupplementsConfig(sortedSupplements);
          
          // Extract categories from supplements
          const uniqueCategories = [...new Set(sortedSupplements
            .flatMap(s => s.categories || [])
            .filter(Boolean))]
            .sort();
            
          setCategories([
            { id: 'all', name: 'All Products', key: 'all' },
            ...uniqueCategories.map(cat => ({
              id: cat,
              name: cat,
              key: cat
            }))
          ]);
          
          // Set initial popular supplements
          setPopularSupplements(sortedSupplements.slice(0, 8));
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') { console.error('Error loading supplements:', error); }
      }
    };
    
    loadSupplements();
  }, []);

  // Handle route-based supplement filtering
  useEffect(() => {
    // No route supplement - check if we should clear selections
    if (!routeSupplementKey) {
      // Check if we came from a route-based filter (stored in sessionStorage)
      const wasRouteFilter = sessionStorage.getItem('marketRouteFilter');
      if (wasRouteFilter) {
        sessionStorage.removeItem('marketRouteFilter');
        setSelectedSupplements([]);
      }
      return;
    }
    
    // We have a route supplement
    if (supplementsConfig.length > 0) {
      const matchingSupplement = supplementsConfig.find(s => s.key === routeSupplementKey);
      
      if (matchingSupplement) {
        const supplementToSelect = {
          key: matchingSupplement.key,
          name: matchingSupplement.name
        };
        
        // Mark that we're using route-based filtering
        sessionStorage.setItem('marketRouteFilter', routeSupplementKey);
        
        // Set this as the ONLY selected supplement
        setSelectedSupplements([supplementToSelect]);
        
        logAnalyticsEvent('route_supplement_filter', {
          item_id: supplementToSelect.key,
          item_name: supplementToSelect.name,
          item_category: 'supplements',
          item_list_context: 'product_explorer_route'
        });
      }
    }
  }, [routeSupplementKey, supplementsConfig]);

  // Handle URL query param filtering (e.g., ?supplements=magnesium,vitamin-d)
  useEffect(() => {
    // Skip if route supplement is set (route takes precedence)
    if (routeSupplementKey) return;
    
    const supplementsParam = searchParams.get('supplements');
    if (!supplementsParam || supplementsConfig.length === 0) return;
    
    // Parse comma-separated supplement keys
    const supplementKeys = supplementsParam.split(',').map(s => s.trim()).filter(Boolean);
    
    if (supplementKeys.length === 0) return;
    
    // Find matching supplements from config
    const supplementsToSelect = supplementKeys
      .map(key => {
        const matchingSupplement = supplementsConfig.find(s => s.key === key);
        return matchingSupplement ? {
          key: matchingSupplement.key,
          name: matchingSupplement.name
        } : null;
      })
      .filter(Boolean);
    
    if (supplementsToSelect.length > 0) {
      // Check if we're returning from a product page (has scroll restoration data)
      const isReturningFromProduct = sessionStorage.getItem('marketClickedProduct');
      
      if (!isReturningFromProduct) {
        // Fresh navigation - scroll to top
        window.scrollTo(0, 0);
      }
      // If returning from product, let the scroll restoration logic handle it
      
      setSelectedSupplements(supplementsToSelect);
      
      if (!isReturningFromProduct) {
        logAnalyticsEvent('query_param_supplement_filter', {
          supplement_keys: supplementKeys,
          supplement_count: supplementsToSelect.length,
          item_category: 'supplements',
          item_list_context: 'product_explorer_query_param'
        });
      }
    }
  }, [searchParams, supplementsConfig, routeSupplementKey]);
  
  // Save state before navigating away
  useEffect(() => {
    const saveMarketState = () => {
      // Only save if we're on /market (not a route-filtered page)
      if (!routeSupplementKey && products.length > 0) {
        sessionStorage.setItem('marketSupplementIndex', currentSupplementIndex.toString());
        // Save which supplements were loaded
        const loadedKeys = [...seenSupplements];
        sessionStorage.setItem('marketLoadedSupplements', JSON.stringify(loadedKeys));
      }
    };
    
    window.addEventListener('beforeunload', saveMarketState);
    
    return () => {
      saveMarketState();
      window.removeEventListener('beforeunload', saveMarketState);
    };
  }, [currentSupplementIndex, seenSupplements, products.length, routeSupplementKey]);
  
  // Function to save clicked product for scroll restoration
  const saveClickedProduct = (productId) => {
    // Save for market view (both regular and query param filtered)
    // Skip only for route-based filtering like /market/:supplementKey
    if (!routeSupplementKey) {
      sessionStorage.setItem('marketClickedProduct', productId);
      sessionStorage.setItem('marketSupplementIndex', currentSupplementIndex.toString());
      const loadedKeys = [...seenSupplements];
      sessionStorage.setItem('marketLoadedSupplements', JSON.stringify(loadedKeys));
      // Tell global ScrollRestoration to skip generic Y restore — we handle it
      sessionStorage.setItem(SKIP_SCROLL_RESTORE_KEY, '1');
    }
  };

  // Discover available supplements (always runs - needed for dropdown)
  useEffect(() => {
    const discoverSupplements = async () => {
      // Skip if already discovered
      if (discoveredSupplements.length > 0) return;
      
      try {
        const { supplements } = await findSupplements({ 
          count: 50,
          sort: 'products'
        });
        
        if (!supplements || supplements.length === 0) {
          if (process.env.NODE_ENV === 'development') { console.log('No supplements found from API'); }
          return;
        }
        
        const supplementsWithNames = supplements.map(item => {
          const matchingSupplement = supplementsConfig.find(s => s.key === item.key);
          return {
            key: item.key,
            name: matchingSupplement ? matchingSupplement.name : item.key
              .split('-')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' '),
            productCount: item.product_count,
            articleCount: item.article_count,
            categories: matchingSupplement ? matchingSupplement.categories : []
          };
        });
        
        supplementsWithNames.sort((a, b) => a.name.localeCompare(b.name));
        setDiscoveredSupplements(supplementsWithNames);
        
      } catch (error) {
        if (process.env.NODE_ENV === 'development') { console.error('Error discovering supplements:', error); }
      }
    };
    
    if (supplementsConfig.length > 0) {
      discoverSupplements();
    }
  }, [supplementsConfig, discoveredSupplements.length]);

  // Load initial products (skip if route has a supplement key or supplements selected)
  useEffect(() => {
    const loadInitialProducts = async () => {
      // Skip if we have selected supplements or if route specifies a supplement
      if (selectedSupplements.length > 0 || routeSupplementKey) return;
      // Skip if supplements not discovered yet
      if (discoveredSupplements.length === 0) return;
      
      setLoading(true);
      try {
        // Check if we're restoring from a previous session
        const savedIndex = sessionStorage.getItem('marketSupplementIndex');
        const savedLoadedSupplements = sessionStorage.getItem('marketLoadedSupplements');
        
        let supplementsToLoad = INITIAL_SUPPLEMENTS_TO_LOAD;
        let supplementKeysToLoad = null;
        
        // If we have saved state, load those supplements instead
        if (savedIndex && savedLoadedSupplements) {
          try {
            supplementKeysToLoad = JSON.parse(savedLoadedSupplements);
            supplementsToLoad = Math.max(parseInt(savedIndex, 10), INITIAL_SUPPLEMENTS_TO_LOAD);
          } catch (e) {
            // Ignore parse errors
          }
        }
        
        // Get supplements to load
        let supplementsForLoading;
        if (supplementKeysToLoad && supplementKeysToLoad.length > 0) {
          // Load previously seen supplements
          supplementsForLoading = discoveredSupplements.filter(s => supplementKeysToLoad.includes(s.key));
          // If we can't find them all, fall back to discovered order
          if (supplementsForLoading.length < supplementKeysToLoad.length) {
            supplementsForLoading = discoveredSupplements.slice(0, supplementsToLoad);
          }
        } else {
          supplementsForLoading = discoveredSupplements.slice(0, supplementsToLoad);
        }
        
        const initialProducts = await loadProductsBatch(supplementsForLoading, 0, supplementsForLoading.length);
        
        if (process.env.NODE_ENV === 'development') { console.log(`Loaded ${initialProducts.length} initial products for ${supplementsForLoading.length} supplements`); }
        
        setProducts(initialProducts);
        setFilteredProducts(initialProducts);
        setCurrentSupplementIndex(supplementsForLoading.length);
        
        // Update seen supplements
        const supplementSet = new Set(supplementsForLoading.map(s => s.key));
        setSeenSupplements(supplementSet);
        
        // Scroll to clicked product after products load
        const clickedProductId = sessionStorage.getItem('marketClickedProduct');
        if (clickedProductId) {
          setTimeout(() => {
            const productElement = document.getElementById(`product-card-${clickedProductId}`);
            if (productElement) {
              // Scroll to element with some offset from top
              const yOffset = -100;
              const y = productElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
              window.scrollTo({ top: y, behavior: 'auto' });
            }
            // Clear saved state after restoring
            sessionStorage.removeItem('marketClickedProduct');
            sessionStorage.removeItem('marketSupplementIndex');
            sessionStorage.removeItem('marketLoadedSupplements');
          }, 200);
        }
        
        // Log initial list view
        logAnalyticsEvent('view_item_list', {
          item_list_name: 'All Products',
          item_category: 'supplements',
          item_list_context: 'product_explorer',
          item_count: initialProducts.length
        });

      } catch (error) {
        if (process.env.NODE_ENV === 'development') { console.error('Error loading initial products:', error); }
      } finally {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    };
    
    loadInitialProducts();
  }, [discoveredSupplements, selectedSupplements.length, routeSupplementKey]);

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = async () => {
      // Skip if we're already loading, have supplements selected, or reached the end
      if (isLoadingMore || loading || selectedSupplements.length > 0 || !hasMoreSupplements) {
        return;
      }

      const scrollPosition = window.innerHeight + window.scrollY;
      const documentHeight = document.documentElement.offsetHeight;
      
      // Check if we've scrolled close to the bottom
      if (documentHeight - scrollPosition < SCROLL_THRESHOLD) {
        if (process.env.NODE_ENV === 'development') { console.log('Scroll threshold reached, loading more products...'); }
        setIsLoadingMore(true);
        
        try {
          // Calculate which supplements to load next
          const nextBatchStart = currentSupplementIndex;
          const nextBatchEnd = currentSupplementIndex + SUPPLEMENTS_PER_BATCH;
          
          // Get the next batch of supplements
          const nextSupplements = discoveredSupplements.slice(nextBatchStart, nextBatchEnd);
          
          if (nextSupplements.length === 0) {
            if (process.env.NODE_ENV === 'development') { console.log('No more supplements to load'); }
            setHasMoreSupplements(false);
            setIsLoadingMore(false);
            return;
          }
          
          if (process.env.NODE_ENV === 'development') { console.log(`Loading products for next ${nextSupplements.length} supplements...`); }
          
          // Use the loadProductsBatch helper to efficiently load products
          const newProducts = await loadProductsBatch(nextSupplements, 0, nextSupplements.length);
          
          if (process.env.NODE_ENV === 'development') { console.log(`Loaded ${newProducts.length} new products`); }
          
          // Update state with new products
          if (newProducts.length > 0) {
            setProducts(prevProducts => {
              // Ensure no duplicates by merging with Map
              const combinedProducts = [...prevProducts, ...newProducts];
              const uniqueProducts = Array.from(
                new Map(combinedProducts.map(product => [product.id, product])).values()
              );
              return uniqueProducts;
            });
            
            // Also update filtered products to match current filters
            setFilteredProducts(prevFiltered => {
              const filteredNewProducts = applyFilters(newProducts, filters);
              const combinedFiltered = [...prevFiltered, ...filteredNewProducts];
              const uniqueFiltered = Array.from(
                new Map(combinedFiltered.map(product => [product.id, product])).values()
              );
              return uniqueFiltered;
            });
            
            // Update seen supplements
            setSeenSupplements(prev => {
              const newSet = new Set(prev);
              nextSupplements.forEach(s => newSet.add(s.key));
              return newSet;
            });
          }
          
          // Always update the current index for the next potential batch
          setCurrentSupplementIndex(nextBatchEnd);
          
          // Now, check if there are more supplements to load based on the new index
          if (nextBatchEnd >= discoveredSupplements.length) {
            setHasMoreSupplements(false);
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') { console.error('Error loading more products:', error); }
        } finally {
          setIsLoadingMore(false);
        }
      }
    };

    // Throttle the scroll handler to avoid too many calls
    let throttleTimer;
    const throttledScrollHandler = () => {
      if (!throttleTimer) {
        throttleTimer = setTimeout(() => {
          handleScroll();
          throttleTimer = null;
        }, 200); // 200ms throttle
      }
    };

    window.addEventListener('scroll', throttledScrollHandler);
    return () => {
      window.removeEventListener('scroll', throttledScrollHandler);
      clearTimeout(throttleTimer);
    };
  }, [
    loading, 
    isLoadingMore, 
    currentSupplementIndex, 
    hasMoreSupplements, 
    discoveredSupplements, 
    selectedSupplements.length,
    filters
  ]);

  // Group supplements by category for better search results
  const groupedSupplements = useMemo(() => {
    const grouped = {};
    supplements.forEach(supplement => {
      (supplement.categories || ['Uncategorized']).forEach(category => {
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push(supplement);
      });
    });
    return grouped;
  }, [supplements]);

  // Improved search filtering with better ranking
  const handleSearchInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    
    if (!newInputValue) {
      setSearchOptions([]);
      return;
    }

    // Filter and rank supplements based on input
    const query = newInputValue.toLowerCase();
    
    // Get filtered supplements with ranking scores
    const filteredWithScores = discoveredSupplements 
      .filter(supplement => 
        supplement.name.toLowerCase().includes(query) ||
        supplement.categories?.some(cat => cat.toLowerCase().includes(query))
      )
      .map(supplement => {
        // Calculate relevance score (higher is better)
        let score = 0;
        
        // Exact match at start gets highest priority
        if (supplement.name.toLowerCase().startsWith(query)) {
          score += 100;
        }
        // Contains the query as a word
        else if (supplement.name.toLowerCase().includes(` ${query}`)) {
          score += 75;
        }
        // Contains the query anywhere
        else if (supplement.name.toLowerCase().includes(query)) {
          score += 50;
        }
        // Category matches
        else if (supplement.categories?.some(cat => cat.toLowerCase().includes(query))) {
          score += 25;
        }
        
        return { supplement, score };
      })
      .sort((a, b) => {
        // First sort by score (descending)
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        // Then sort alphabetically by name (ascending)
        return a.supplement.name.localeCompare(b.supplement.name);
      }) // Sort by score (descending) and then by name (ascending)
      .map(item => item.supplement)
      .slice(0, 10); // Limit to 10 results
    
    // Update search options with filtered results
    setSearchOptions(filteredWithScores);
  };

  // Option selection handler
  const handleOptionSelect = (event, supplement) => {
    if (supplement) {
      // Find the matching supplement from config to get proper name
      const matchingSupplement = supplementsConfig.find(s => s.key === supplement.key);
      const supplementToAdd = {
        key: supplement.key,
        name: matchingSupplement ? matchingSupplement.name : supplement.name
      };
      
      // Check if already selected
      const alreadySelected = selectedSupplements.some(s => s.key === supplementToAdd.key);
      
      if (!alreadySelected) {
        // Add to selected supplements
        setSelectedSupplements(prev => [...prev, supplementToAdd]);
        
        // Log selection
        logAnalyticsEvent('select_supplement_search', {
          item_id: supplementToAdd.key,
          item_name: supplementToAdd.name,
          item_category: 'supplements',
          item_list_context: 'product_explorer_search'
        });
        
        // If we're on a route-based filter and adding a different supplement,
        // navigate to /market to show multiple supplements
        if (routeSupplementKey && supplement.key !== routeSupplementKey) {
          navigate('/market');
        }
      }

      setSearchQuery('');
      setInputValue('');
      setSearchOptions([]);
    }
  };

  // Function to handle removing a supplement from selection
  const handleRemoveSupplement = (supplementKey) => {
    let removedItem = null;
    let remainingCount = 0;
    
    setSelectedSupplements(prev => {
      removedItem = prev.find(s => s.key === supplementKey);
      const newList = prev.filter(s => s.key !== supplementKey);
      remainingCount = newList.length;
      return newList;
    });
    
    // Navigate to /market to clear URL params if:
    // 1. Removing the supplement from a route-based filter, OR
    // 2. We have query params and this is the last supplement
    const hasQueryParams = searchParams.get('supplements');
    if (supplementKey === routeSupplementKey || (hasQueryParams && remainingCount === 0)) {
      navigate('/market');
    } else if (hasQueryParams && remainingCount > 0) {
      // Update URL to reflect remaining supplements
      const remaining = selectedSupplements
        .filter(s => s.key !== supplementKey)
        .map(s => s.key)
        .join(',');
      navigate(`/market?supplements=${remaining}`, { replace: true });
    }
    
    // Log deselection
    if (removedItem) {
      logAnalyticsEvent('remove_supplement_chip', {
        item_id: removedItem.key,
        item_name: removedItem.name,
        item_category: 'supplements',
        item_list_context: 'product_explorer_chips'
      });
    }
  };

  // Function to handle adding a supplement from the chips
  const handleAddSupplementChip = (supplement) => {
    const matchingSupplement = supplementsConfig.find(s => s.key === supplement.key);
    const supplementToAdd = {
      key: supplement.key,
      name: matchingSupplement ? matchingSupplement.name : supplement.name
    };
    
    let added = false;
    setSelectedSupplements(prev => {
      if (!prev.some(s => s.key === supplementToAdd.key)) {
        added = true;
        return [...prev, supplementToAdd];
      }
      return prev;
    });

    // Log selection if added
    if (added) {
      logAnalyticsEvent('click_quick_select_chip', {
        item_id: supplementToAdd.key,
        item_name: supplementToAdd.name,
        item_category: 'supplements',
        item_list_context: 'product_explorer_quick_select'
      });
    }
  };

  // Update the filtering logic
  useEffect(() => {
    if (!loading && products.length > 0) {
      let filtered = applyFilters(products, filters);
      
      setFilteredProducts(filtered);
      const newTotalPages = Math.ceil(filtered.length / productsPerPage);
      setTotalPages(newTotalPages);
      
      // Adjust current page if needed
      if (page > newTotalPages) {
        setPage(1);
      }
    }
  }, [products, filters, loading, productsPerPage]);
  
  // Handle category change
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    // Reset search when changing category
    setSearchQuery('');
  };
  
  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };
  
  // Function to load products for a specific supplement
  const loadProductsForSupplement = async (supplementKey) => {
    // Find the matching supplement from config
    const matchingSupplement = supplementsConfig.find(s => s.key === supplementKey);
    
    if (matchingSupplement) {
      // First, add to selected supplements if not already there
      setSelectedSupplements(prev => {
        if (!prev.some(s => s.key === supplementKey)) {
          return [...prev, {
            key: supplementKey,
            name: matchingSupplement.name
          }];
        }
        return prev;
      });
    } else {
      if (process.env.NODE_ENV === 'development') { console.warn(`Supplement with key ${supplementKey} not found in configuration`); }
    }
  };
  
  // Popular supplement click handler
  const handlePopularSupplementClick = (supplement) => {
    setSearchQuery(supplement.name);
    setActiveCategory('all');
    loadProductsForSupplement(supplement.key);
  };
  
  // Reset filters
  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    // Log filter reset
    logAnalyticsEvent('reset_filters', {
      item_list_context: 'product_explorer'
    });
  };
  
  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    // Log filter application
    logAnalyticsEvent('apply_filters', {
      filters: newFilters, // Log the whole filter object
      filter_count: getFilterCount(newFilters),
      item_list_context: 'product_explorer'
    });
  };
  
  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    // Scroll to top when changing page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Load products when selected supplements change
  useEffect(() => {
    const loadProductsForSelectedSupplements = async () => {
      if (selectedSupplements.length === 0) return;
      
      setLoading(true);
      try {
        // Load products for each selected supplement in parallel
        const productPromises = selectedSupplements.map(async (supplement) => {
          // Check cache first
          const cached = productCache.get(supplement.key);
          if (cached && Date.now() - cached.timestamp < PRODUCT_CACHE_TTL) {
            return cached.products;
          }
          
          // Fetch from API if not in cache
          const products = await searchProductsForSupplement(supplement.key);
          
          // Add source information to products
          const productsWithSource = products.map(p => ({
            ...p,
            sourceSupplement: supplement.name,
            supplementKey: supplement.key,
            id: p.id || `${supplement.key}-${Math.random().toString(36).substring(2, 10)}`
          }));
          
          // Cache the results
          productCache.set(supplement.key, {
            products: productsWithSource,
            timestamp: Date.now()
          });
          
          return productsWithSource;
        });
        
        // Wait for all product fetches to complete
        const productResults = await Promise.all(productPromises);
        
        // Combine and deduplicate products
        const allProducts = productResults.flat();
        const uniqueProducts = Array.from(
          new Map(allProducts.map(product => [product.id, product])).values()
        );
        
        // Update state
        setProducts(uniqueProducts);
        setFilteredProducts(applyFilters(uniqueProducts, filters));
        
        if (process.env.NODE_ENV === 'development') { console.log(`Loaded ${uniqueProducts.length} products for ${selectedSupplements.length} selected supplements`); }
        
        // Scroll restoration for returning from product page
        const clickedProductId = sessionStorage.getItem('marketClickedProduct');
        if (clickedProductId) {
          setTimeout(() => {
            const productElement = document.getElementById(`product-card-${clickedProductId}`);
            if (productElement) {
              const yOffset = -100;
              const y = productElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
              window.scrollTo({ top: y, behavior: 'auto' });
            }
            // Clear saved state after restoring
            sessionStorage.removeItem('marketClickedProduct');
          }, 200);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') { console.error('Error loading products for selected supplements:', error); }
      } finally {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    };
    
    loadProductsForSelectedSupplements();
  }, [selectedSupplements, filters]);

  // Compute all SEO metadata dynamically based on the current route
  const seoData = useMemo(() => {
    if (!routeSupplementKey) {
      return {
        title: 'Shop Supplements | myStack Market',
        description: 'Shop vetted supplement products. Browse by supplement type, filter by certifications and dietary preferences. myStack Market.',
        canonicalUrl: 'https://my-stack.ai/market',
        ogImage: null,
        isSupplementPage: false,
        supplementName: null,
      };
    }

    const supplementName = selectedSupplements[0]?.name || formatSupplementName(routeSupplementKey);
    const productCount = products.length;
    const countText = productCount > 0 ? `${productCount} ` : '';
    const ogImage = products[0]?.image_url || null;
    const encodedSupplementKey = encodePathSegment(routeSupplementKey);

    return {
      title: `Best ${supplementName} Supplements | Compare & Shop | myStack Market`,
      description: `Shop ${countText}vetted ${supplementName} supplement products. Filter by certifications, dosage, and dietary preferences. Evidence-based recommendations from myStack.`,
      canonicalUrl: `https://my-stack.ai/market/${encodedSupplementKey}`,
      ogImage,
      isSupplementPage: true,
      supplementName,
      productCount,
    };
  }, [routeSupplementKey, selectedSupplements, products]);

  // Build JSON-LD schemas for the current page
  const pageSchemas = useMemo(() => {
    const schemaList = [];

    if (seoData.isSupplementPage) {
      schemaList.push({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": seoData.title,
        "description": seoData.description,
        "url": seoData.canonicalUrl,
      });

      if (products.length > 0) {
        schemaList.push({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": `Best ${seoData.supplementName} Supplements`,
          "itemListElement": products.slice(0, 10).map((p, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": p.product_name,
            "url": `https://my-stack.ai/market/${encodePathSegment(routeSupplementKey)}/${generateProductSlug(p)}`,
          })),
        });
      }
    } else {
      schemaList.push({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Shop Supplements | myStack Market",
        "description": "Shop vetted supplement products. Browse by supplement type, filter by certifications and dietary preferences.",
        "url": "https://my-stack.ai/market",
      });
    }

    return schemaList;
  }, [seoData, products, routeSupplementKey]);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 1, sm: 2, md: 3 } }}>
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <link rel="canonical" href={seoData.canonicalUrl} />
        {seoData.isSupplementPage && <meta property="og:type" content="website" />}
        {seoData.isSupplementPage && <meta property="og:title" content={seoData.title} />}
        {seoData.isSupplementPage && <meta property="og:description" content={seoData.description} />}
        {seoData.isSupplementPage && <meta property="og:url" content={seoData.canonicalUrl} />}
        {seoData.isSupplementPage && seoData.ogImage && <meta property="og:image" content={seoData.ogImage} />}
        {seoData.isSupplementPage && <meta name="twitter:card" content="summary_large_image" />}
        {seoData.isSupplementPage && <meta name="twitter:title" content={seoData.title} />}
        {seoData.isSupplementPage && <meta name="twitter:description" content={seoData.description} />}
        {seoData.isSupplementPage && seoData.ogImage && <meta name="twitter:image" content={seoData.ogImage} />}
        {pageSchemas.map((schema, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(schema)}</script>
        ))}
      </Helmet>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={styles.root}>
            <Box sx={styles.contentContainer}>

              <Box sx={styles.searchBar}>
                <Autocomplete
                  freeSolo
                  options={searchOptions.length > 0 ? searchOptions : discoveredSupplements}
                  getOptionLabel={(option) => option.name || option}
                  onInputChange={handleSearchInputChange}
                  onChange={handleOptionSelect}
                  value={null}
                  inputValue={inputValue}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={selectedSupplements.length > 0 ? "Add another supplement..." : "Search supplements by name..."}
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              {/* Selected supplements chips */}
              {selectedSupplements.length > 0 && (
                <Box sx={styles.searchChips}>
                  {selectedSupplements.map((supplement) => (
                    <Chip
                      key={supplement.key}
                      label={supplement.name}
                      onDelete={() => handleRemoveSupplement(supplement.key)}
                      color="primary"
                      variant="outlined"
                      sx={{ borderRadius: '1rem' }}
                    />
                  ))}
                  <Chip
                    label="Clear All"
                    onClick={() => {
                      setSelectedSupplements([]);
                      setSearchOptions([]);
                      setInputValue('');
                      // Log clear all supplements
                      logAnalyticsEvent('click_clear_all_supplements', {
                        item_category: 'supplements',
                        item_list_context: 'product_explorer_chips'
                      });
                      // Navigate to /market if we have route-based or query param filters
                      if (routeSupplementKey || searchParams.get('supplements')) {
                        navigate('/market');
                      }
                    }}
                    color="default"
                    variant="outlined"
                    sx={{ borderRadius: '1rem' }}
                  />
                </Box>
              )}

              {/* Popular supplements section */}
              {discoveredSupplements.length > 0 && !selectedSupplements.length && (
                <Box sx={styles.supplementsSection}>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    color: 'text.secondary',
                    fontSize: '0.85rem',
                    letterSpacing: '0.01em'
                  }}>
                    Quick select:
                  </Typography>
                  <Box sx={styles.supplementChips}>
                    {discoveredSupplements.slice(0, 10).map((supplement) => {
                      const matchingSupplement = supplementsConfig.find(s => s.key === supplement.key);
                      const displayName = matchingSupplement ? matchingSupplement.name : supplement.name;
                      const isSelected = selectedSupplements.some(s => s.key === supplement.key);
                      
                      return (
                        <Tooltip 
                          key={supplement.key}
                          title={isSelected ? "Already selected" : `Add ${displayName}`}
                          arrow
                          placement="top"
                        >
                          <span>
                            <Chip
                              label={displayName}
                              onClick={() => !isSelected && handleAddSupplementChip({
                                ...supplement,
                                name: displayName
                              })}
                              color={isSelected ? "primary" : "default"}
                              disabled={isSelected}
                              size="small"
                              sx={{ 
                                opacity: isSelected ? 0.7 : 1,
                                cursor: isSelected ? 'default' : 'pointer',
                                borderRadius: radius.md,
                                transition: 'all 0.15s ease',
                                '&:hover': !isSelected ? {
                                  backgroundColor: 'rgba(0, 0, 0, 0.06)',
                                  transform: `translateY(-${r(1)})`
                                } : {},
                                height: '1.5rem',
                                fontSize: '0.75rem',
                                fontWeight: 400,
                                backgroundColor: isSelected ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.03)',
                                color: isSelected ? theme.palette.primary.contrastText : 'text.primary',
                                boxShadow: 'none',
                                border: `${r(1)} solid`,
                                borderColor: isSelected ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.06)',
                                '& .MuiChip-label': {
                                  padding: '0 0.5rem'
                                }
                              }}
                            />
                          </span>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Filter section */}
              <ClickAwayListener onClickAway={() => setFiltersExpanded(false)}>
                <Box sx={styles.filterContainer}>
                  <Box sx={{
                    ...styles.filterHeader,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center'
                  }}>
                    <Badge
                      badgeContent={filterCount}
                      color="primary"
                      sx={{ 
                        '& .MuiBadge-badge': { 
                          fontSize: '0.7rem', 
                          minWidth: '1.125rem',
                          height: '1.125rem',
                          fontWeight: 600
                        } 
                      }}
                    >
                      <Button
                        startIcon={<TuneIcon />}
                        endIcon={filtersExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        size="small"
                        variant={filtersExpanded ? "contained" : "outlined"}
                        onClick={() => {
                          const newState = !filtersExpanded;
                          setFiltersExpanded(newState);
                          // Log filter toggle
                          logAnalyticsEvent('click_filter_button', {
                            expanded: newState,
                            item_list_context: 'product_explorer'
                          });
                        }}
                        sx={{ 
                          minWidth: 'unset',
                          borderRadius: '0.9rem',
                          p: '0.46rem 0.92rem',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.88rem',
                          letterSpacing: '0.01em',
                          borderColor: filtersExpanded
                            ? alpha(theme.palette.primary.main, 0.4)
                            : alpha(theme.palette.primary.main, 0.28),
                          color: filtersExpanded ? theme.palette.primary.contrastText : color.evergreen,
                          backgroundColor: filtersExpanded
                            ? theme.palette.primary.main
                            : alpha(theme.palette.background.paper, 0.95),
                          boxShadow: filtersExpanded
                            ? `0 0.28rem 0.9rem ${alpha(theme.palette.primary.main, 0.3)}`
                            : `0 0.18rem 0.6rem ${alpha(theme.palette.primary.main, 0.12)}`,
                          '& .MuiButton-startIcon .MuiSvgIcon-root': {
                            fontSize: '1.05rem',
                          },
                          '& .MuiButton-endIcon .MuiSvgIcon-root': {
                            fontSize: '1.05rem',
                          },
                          '&:hover': {
                            backgroundColor: filtersExpanded
                              ? theme.palette.primary.dark
                              : alpha(theme.palette.primary.main, 0.08),
                            borderColor: alpha(theme.palette.primary.main, 0.36),
                            boxShadow: `0 0.3rem 0.95rem ${alpha(theme.palette.primary.main, 0.18)}`,
                          },
                          '&:active': {
                            transform: 'scale(0.98)',
                          },
                        }}
                      >
                        Filter
                      </Button>
                    </Badge>
                  </Box>
                  
                  {/* Active Filter Chips - Always visible when filters are active */}
                  {filterCount > 0 && (
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: 1, 
                      p: { xs: 1, sm: 1.5 },
                      mt: 1.5,
                      width: '100%',
                      borderRadius: '0.5rem',
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      border: `${r(1)} solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    }}>
                      {/* Quality filters */}
                      {filters.third_party_tested && (
                        <Chip
                          label="Third-Party Tested"
                          size="small"
                          onDelete={() => handleFilterChange({ ...filters, third_party_tested: false })}
                          color="primary"
                          variant="outlined"
                          sx={{ 
                            borderRadius: '0.375rem',
                            height: { xs: '1.5rem', sm: '1.75rem' },
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          }}
                        />
                      )}
                      
                      {filters.gmp_certified_facility && (
                        <Chip
                          label="GMP Certified"
                          size="small"
                          onDelete={() => handleFilterChange({ ...filters, gmp_certified_facility: false })}
                          color="primary"
                          variant="outlined"
                          sx={{ 
                            borderRadius: '0.375rem',
                            height: { xs: '1.5rem', sm: '1.75rem' },
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          }}
                        />
                      )}
                      
                      {filters.has_certifications && (
                        <Chip
                          label="Has Certifications"
                          size="small"
                          onDelete={() => handleFilterChange({ ...filters, has_certifications: false })}
                          color="primary"
                          variant="outlined"
                          sx={{ 
                            borderRadius: '0.375rem',
                            height: { xs: '1.5rem', sm: '1.75rem' },
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          }}
                        />
                      )}
                      
                      {/* Dietary filters */}
                      {Object.entries(DIETARY_FIELD_MAP).map(([label, fieldName]) => (
                        filters[fieldName] && (
                          <Chip
                            key={fieldName}
                            label={label}
                            size="small"
                            onDelete={() => {
                              const newFilters = { ...filters };
                              newFilters[fieldName] = false;
                              handleFilterChange(newFilters);
                            }}
                            color="primary"
                            variant="outlined"
                            sx={{ 
                              borderRadius: '0.375rem',
                              height: { xs: '1.5rem', sm: '1.75rem' },
                              fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            }}
                          />
                        )
                      ))}
                      
                      {/* Form factors */}
                      {filters.formFactors?.map(form => (
                        <Chip
                          key={form}
                          label={form}
                          size="small"
                          onDelete={() => {
                            handleFilterChange({
                              ...filters,
                              formFactors: filters.formFactors.filter(f => f !== form)
                            });
                          }}
                          color="primary"
                          variant="outlined"
                          sx={{ 
                            borderRadius: '0.375rem',
                            height: { xs: '1.5rem', sm: '1.75rem' },
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          }}
                        />
                      ))}
                      
                      <Button
                        size="small"
                        startIcon={<ClearIcon fontSize="small" />}
                        onClick={handleResetFilters}
                        sx={{ 
                          textTransform: 'none', 
                          ml: 'auto',
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          fontWeight: 500,
                          color: theme.palette.text.secondary,
                          borderRadius: radius.sm,
                          py: { xs: 0.3, sm: 0.4 },
                          px: { xs: 1, sm: 1.5 },
                          minWidth: { xs: r(60), sm: r(80) },
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.error.main, 0.08),
                            color: theme.palette.error.main,
                          }
                        }}
                      >
                        Clear all
                      </Button>
                    </Box>
                  )}

                  {/* Use Collapse for smooth transition */}
                  <Collapse in={filtersExpanded} timeout="auto" unmountOnExit>
                    <ProductFilters
                      filters={filters}
                      onFilterChange={handleFilterChange}
                      onResetFilters={handleResetFilters}
                      filterCount={filterCount}
                      onClose={() => setFiltersExpanded(false)}
                      hideFilterChips={true}
                    />
                  </Collapse>
                </Box>
              </ClickAwayListener>

              {/* Loading state */}
              {loading && (
                <Box sx={styles.loadingContainer}>
                  {products.length === 0 && (
                    <Typography color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
                      {loadingDuration < 3000 
                        ? 'Loading products...'
                        : loadingDuration < 8000
                          ? 'Almost there...'
                          : 'Taking longer than usual. Thanks for your patience!'
                      }
                    </Typography>
                  )}
                  <CircularProgress size={48} />
                </Box>
              )}

              {/* Products display */}
              {!loading && (
                <>
                  {products.length === 0 ? (
                    // Show spinner if initial load hasn't completed yet, or restoring from session
                    !initialLoadComplete || sessionStorage.getItem('marketClickedProduct') || sessionStorage.getItem('marketLoadedSupplements') ? (
                      <Box sx={styles.loadingContainer}>
                        <CircularProgress size={48} />
                      </Box>
                    ) : (
                    <Box sx={styles.noResults}>
                      {selectedSupplements.length > 0 ? (
                        // Message when specific supplements are selected but no products found
                        <>
                          <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                            No products found for {selectedSupplements.map(s => s.name).join(', ')}
                          </Typography>
                          <Typography color="text.secondary" sx={{ mb: 3, maxWidth: r(500), mx: 'auto' }}>
                            We haven't found products for these supplements yet. Try searching for different supplements or check back later.
                          </Typography>
                          <Button 
                            variant="outlined" 
                            onClick={() => setSelectedSupplements([])}
                            size="medium"
                            sx={{
                              borderRadius: radius.sm,
                              px: 3,
                              py: 1,
                              textTransform: 'none',
                              fontWeight: 500
                            }}
                          >
                            Clear Selection
                          </Button>
                        </>
                      ) : (
                        // General message when no products found during initial load
                        <>
                          <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                            No supplement products found
                          </Typography>
                          <Typography color="text.secondary" sx={{ mb: 3, maxWidth: r(500), mx: 'auto' }}>
                            Try searching for a specific supplement above or select from the popular supplements
                          </Typography>
                          {discoveredSupplements.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', maxWidth: r(400), mx: 'auto', mt: 2 }}>
                              {discoveredSupplements.slice(0, 3).map(supplement => (
                                <Chip
                                  key={supplement.key}
                                  label={supplement.name}
                                  onClick={() => handleAddSupplementChip(supplement)}
                                  color="primary"
                                  variant="outlined"
                                  sx={{ borderRadius: radius.lg }}
                                />
                              ))}
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                    )
                  ) : (
                    <>
                      {selectedSupplements.length > 0 ? (
                        // Display all products in a single grid when supplements are selected
                        <>
                          <Typography 
                            sx={{ 
                              mb: 2, 
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              fontWeight: 500,
                              color: 'text.secondary',
                              letterSpacing: '0.015em',
                              textTransform: 'uppercase'
                            }} 
                            variant="h4"
                          >
                            {selectedSupplements.length === 1 
                              ? `${selectedSupplements[0].name} Products` 
                              : 'Products for Selected Supplements'}
                          </Typography>
                          <Box sx={styles.productsGrid}>
                            {getCurrentPageProducts().map((product) => (
                              <ProductCard 
                                key={product.id}
                                product={product}
                                compact={isMobile || isTablet}
                                onBeforeNavigate={saveClickedProduct}
                              />
                            ))}
                          </Box>
                        </>
                      ) : (
                        // Display products grouped by supplement when no specific supplement is selected
                        <>
                          {Object.entries(
                            // Group products by supplement name
                            getCurrentPageProducts().reduce((acc, product) => {
                              // Ensure we use the proper supplement name from the configuration
                              const matchingSupplement = supplementsConfig.find(s => s.key === product.supplementKey);
                              // Use the proper display name from config, fallback to titlecased source
                              const supplementName = matchingSupplement ? matchingSupplement.name : 
                                (product.sourceSupplement || product.supplementKey?.split('-')
                                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                  .join(' '));
                                  
                              if (!acc[supplementName]) {
                                acc[supplementName] = [];
                              }
                              acc[supplementName].push(product);
                              return acc;
                            }, {})
                          ).map(([supplementName, supplementProducts]) => (
                            <Box key={supplementName} sx={styles.supplementSection}>
                              <Typography sx={styles.supplementTitle}>
                                {supplementName}
                                <Typography component="span" sx={styles.supplementCount}>
                                  {supplementProducts.length} products
                                </Typography>
                              </Typography>
                              <Box sx={styles.productsGrid}>
                                {supplementProducts.map((product) => (
                                  <ProductCard 
                                    key={product.id}
                                    product={product}
                                    compact={isMobile || isTablet}
                                    onBeforeNavigate={saveClickedProduct}
                                  />
                                ))}
                              </Box>
                            </Box>
                          ))}
                        </>
                      )}
                    </>
                  )}
                  
                  {isLoadingMore && !selectedSupplements.length && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                      <CircularProgress size={32} />
                    </Box>
                  )}
                  
                  {!hasMoreSupplements && !selectedSupplements.length && products.length > 0 && (
                    <Typography 
                      sx={{ textAlign: 'center', mt: 4, mb: 2, color: 'text.secondary' }}
                    >
                      You've seen all available products
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
      
      {/* Back to Top button - positioned above bottom nav */}
      <BackToTop threshold={500} bottom={80} />
    </Container>
  );
};

export default ProductsExplorer; 